import {
  ButtonType,
  Div,
  PricingTile,
  RadioOptionCardProps,
  Separator,
  Spacer,
  UpsaleSheet,
} from '@sendoutcards/quantum-design-ui'
import React, { useMemo, useState } from 'react'
import { Transition } from 'src/chrome'
import connect from 'src/redux/connect'
import { openApp } from 'src/helpers/appHelpers'
import FeaturedCardsGrid from '../components/FeaturedCardsGrid'
import { Disclaimer } from 'src/marketing/plans/components'
import { openLaunchPage, openPlans } from 'src/redux/actions/routeAliases'
import { getCardCatalog } from 'src/redux/selectors/catalog'
import { CardCatalog } from 'src/catalog/types'
import Future from 'src/utils/Future'

type PromoProps = {
  promo?: string
  openPlans: () => void
  openLaunchPage: () => void
  cardCatalog: Future<CardCatalog>
}

type ExpandedOptionChild = {
  expandedChild?: React.ReactNode
}

type OptionType = ExpandedOptionChild & RadioOptionCardProps

const discountDisclaimer = [
  '* Discounts apply to most gifts. Excludes: books, apparel, and business-building tools.',
]

const options: OptionType[] = [
  {
    title: '$97.00/Month',
    subtitle: 'For unlimited single card send',
    isFeatured: true,
    optionId: '3', // plan ID from Plan table
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            {
              feature: 'Send Unlimited Heartfelt Prompting Cards Each Month!',
              subFeatures: [
                'Unlimited Postcards (Postage included!)',
                'Unlimited Two-Panel Cards (Postage included!)',
                'A Heartfelt Prompting Card has an immediate send with a unique message to a single recipient.',
              ],
            },
            {
              feature: '30 System Sends each month',
              subFeatures: ['$1.50 per card beyond the included 30 cards.'],
            },
            'Premier discounted shipping on top-selling gifts',
            'Save 30 percent discount on our bestselling gifts*',
            'Premium Digital Storage for pictures and addresses',
            'Postage not included on any System Sends',
          ]}
          title="Unlimited"
          billingInterval="Monthly"
          price="$97"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
  {
    title: '$37.00/Month',
    subtitle: 'Send one free card per day',
    isFeatured: false,
    optionId: '6',
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            {
              feature: 'Send 30 Postcards & Two-Panel Cards Each Month!',
              subFeatures: [
                'Includes both Heartfelt and System Sends.',
                '$1.75 per card beyond the included 30 cards.',
              ],
            },
            'Save 20 percent on our bestselling gifts*',
            'Enhanced Digital Storage for pictures and addresses',
            'Postage not included on any card sends',
          ]}
          title="Card a Day"
          billingInterval="Monthly"
          price="$37"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
]

const getPlan = (planId: string) => {
  return options.find(option => option.optionId === planId)
}

const Promo: React.FC<PromoProps> = props => {
  const { promo, openPlans, openLaunchPage, cardCatalog } = props
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [promoId] = useState(promo)
  const [shouldGoToPricing, setShouldGoToPricing] = useState(false)
  const promoOptionId =
    options.find(option => option.optionId === promoId)?.optionId || '3'
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>(
    shouldGoToPricing
      ? options
      : options.filter(option => option.optionId === promoOptionId),
  )
  const upsaleSheetTitle = useMemo(() => {
    return promoId === '3' ? 'Send Unlimited Postcards!' : 'Want to send more?'
  }, [promoId])
  const upsaleSheetDescription = useMemo(() => {
    return promoId === '3'
      ? 'To send as many postcards as you want and to access some great benefits, choose our $97 Unlimited subscription! Click Subscribe Now to sign up or view other options!'
      : 'To send more cards and to access some great benefits, upgrade to one of our paid accounts. You can send as many cards as you want (yes, thats right) on our $97 Unlimited subscription! Or a card a day on our $37 subscription! Click to sign up or view our other packages!'
  }, [promoId])

  const submitAction = {
    title: 'Subscribe Now',
    type: 'secondary' as ButtonType,
    onClick: (optionId: string) => {
      setIsRedirecting(true)
      openApp('', {
        join: '',
        promo: getPlan(optionId)?.optionId ?? '3',
      })
    },
  }

  const handleClose = () => {
    setIsRedirecting(false)
    openLaunchPage()
  }

  const handleViewMore = () => {
    if (shouldGoToPricing) {
      setIsRedirecting(true)
      openPlans()
    } else {
      setShouldGoToPricing(true)
      setFilteredOptions(options)
    }
  }

  const viewMoreLink = {
    onClick: () => handleViewMore(),
    title: 'View More Options >',
  }

  if (isRedirecting || !cardCatalog.value) {
    return (
      <Transition
        message="Loading..."
        messageStyle={{ color: 'black', fontWeight: 500 }}
      />
    )
  }

  return (
    <UpsaleSheet
      setIsOpen={() => handleClose()}
      isOpen={true}
      title={upsaleSheetTitle}
      description={upsaleSheetDescription}
      options={filteredOptions}
      submitAction={submitAction}
      extraLink={viewMoreLink}
      selectedOptionTitle="Great choice"
      selectedOptionDescription="We are so happy with your choice"
      backgroundElement={
        <FeaturedCardsGrid
          featuredCards={
            cardCatalog.value ? cardCatalog.value.featuredCards : []
          }
        />
      }
      selectedOption={''}
    />
  )
}

export default connect(
  state => {
    return { promo: state.promo.promo, cardCatalog: getCardCatalog(state) }
  },
  { openPlans, openLaunchPage },
)(Promo)
